import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Divider,
  Stack,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Badge,
  LinearProgress,
  Fade,
  Zoom,
  Slide,
  Grow,
  Collapse
} from '@mui/material';
import {
  Home,
  ListAlt,
  ArrowBack,
  Edit,
  PlayArrow,
  CheckCircle,
  Cancel,
  PhotoCamera,
  Refresh,
  Visibility,
  Download,
  Person,
  Schedule,
  LocationOn,
  Assignment,
  Bed,
  PriorityHigh,
  History,
  Warning,
  Info,
  Task,
  Room,
  Timeline,
  PhotoLibrary,
  CloudUpload,
  Star,
  StarBorder,
  Chat,
  Phone,
  Email,
  Work,
  CalendarToday,
  AccessTime,
  ExpandMore,
  ExpandLess,
  ThumbUp,
  ThumbDown,
  Grade,
  Speed,
  DoneAll,
  Pending,
  HourglassEmpty,
  Error,
  Notifications,
  LocalHospital,
  CleaningServices
} from '@mui/icons-material';
import { getTaskById, updateTaskStatus, addPhotos, getAuditsByTask } from '../services/api';
import { useSnackbar } from 'notistack';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_URL_FOR_FILE } from '../../../../../utils/BaseUrl';

export const TaskDetail = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [expandedSection, setExpandedSection] = useState({
    timeline: true,
    checklist: true,
    audits: true
  });

  const loadTaskData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [taskRes, auditsRes] = await Promise.all([
        getTaskById(id),
        getAuditsByTask(id, { page: 1, limit: 50 }).catch(err => {
          console.warn('Could not load audits:', err);
          return { data: { audits: [] } };
        })
      ]);

      // Handle task data
      if (taskRes.data && taskRes.data.task) {
        setTask(taskRes.data.task);
      } else if (taskRes.data) {
        setTask(taskRes.data);
      } else {
        setTask(taskRes);
      }

      // Handle audits data
      if (auditsRes.data) {
        setAudits(auditsRes.data.audits || auditsRes.data.data || auditsRes.data || []);
      } else {
        setAudits(auditsRes.audits || auditsRes || []);
      }

    } catch (err) {
      console.error('Load task error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load task details';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadTaskData();
    } else {
      setError('No task ID provided');
      setLoading(false);
    }
  }, [id]);

  const handleStatusUpdate = async () => {
    try {
      await updateTaskStatus(id, { status: selectedStatus });
      enqueueSnackbar(`Task status updated to ${selectedStatus.replace('_', ' ')}`, { variant: 'success' });
      setStatusDialogOpen(false);
      loadTaskData();
    } catch (err) {
      console.error('Status update error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update status';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleFileUpload = async (before = true) => {
    if (files.length === 0) {
      enqueueSnackbar('Please select files first', { variant: 'warning' });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      formData.append('before', before.toString());

      await addPhotos(id, formData);
      enqueueSnackbar(`${before ? 'Before' : 'After'} photos uploaded successfully`, { variant: 'success' });
      setFiles([]);
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      loadTaskData();
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.response?.data?.message || 'Upload failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleFilesChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'cancelled': return 'error';
      case 'overdue': return 'error';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_progress': return <PlayArrow />;
      case 'completed': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      case 'pending': return <HourglassEmpty />;
      case 'overdue': return <Error />;
      default: return <Pending />;
    }
  };

  const getTaskTypeIcon = (taskType) => {
    switch (taskType) {
      case 'cleaning': return <CleaningServices />;
      case 'deep_cleaning': return <CleaningServices color="primary" />;
      case 'disinfection': return <LocalHospital />;
      case 'linen_change': return <Bed />;
      case 'maintenance': return <Work />;
      case 'inspection': return <Assignment />;
      default: return <Task />;
    }
  };

  const isOverdue = () => {
    return task && task.status !== 'completed' &&
      task.status !== 'cancelled' &&
      new Date(task.dueAt) < new Date();
  };

  const toggleSection = (section) => {
    setExpandedSection(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Grow in={true}>
          <Paper elevation={24} sx={{ 
            p: 6, 
            borderRadius: 4, 
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            maxWidth: 400,
            textAlign: 'center'
          }}>
            <CircularProgress 
              size={80} 
              thickness={4} 
              sx={{ 
                mb: 3,
                color: 'primary.main'
              }} 
            />
            <Typography variant="h4" sx={{ 
              fontSize: '1.8rem', 
              fontWeight: 800,
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Loading Task Details
            </Typography>
            <Typography variant="h6" sx={{ 
              fontSize: '1.2rem', 
              color: 'text.secondary',
              mb: 3
            }}>
              Please wait while we fetch the task information...
            </Typography>
            <LinearProgress 
              variant="indeterminate" 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
              }} 
            />
          </Paper>
        </Grow>
      </Box>
    );
  }

  if (error || !task) {
    return (
      <Box sx={{ 
        p: 3, 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
      }}>
        <Slide direction="up" in={true}>
          <Card sx={{ 
            maxWidth: 600, 
            mx: 'auto', 
            mt: 8,
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              p: 4, 
              background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)',
              textAlign: 'center',
              color: 'white'
            }}>
              <Error sx={{ fontSize: 80, mb: 2 }} />
              <Typography variant="h3" fontWeight={800} sx={{ mb: 1 }}>
                Task Not Found
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {error || 'The requested task could not be loaded'}
              </Typography>
            </Box>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{ 
                  px: 4, 
                  py: 1.5,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Back to Task List
              </Button>
            </CardContent>
          </Card>
        </Slide>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      {/* Breadcrumbs */}
      <Paper elevation={2} sx={{ 
        p: 2, 
        mb: 3, 
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)'
      }}>
        <Breadcrumbs sx={{ fontSize: '1.1rem' }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/hospital')}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: 'primary.main' }
            }}
          >
            <Home sx={{ fontSize: 20 }} />
            Hospital
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/hospital/housekeeping-and-linen-management/housekeeping/task-list')}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: 'primary.main' }
            }}
          >
            <CleaningServices sx={{ fontSize: 20 }} />
            Housekeeping
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/hospital/housekeeping-and-linen-management/housekeeping/task-list')}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: 'primary.main' }
            }}
          >
            <ListAlt sx={{ fontSize: 20 }} />
            Task List
          </Link>
          <Typography color="primary.main" sx={{ 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            <Task sx={{ fontSize: 20 }} />
            Task {task.taskNumber || `#${task._id?.slice(-6)}`}
          </Typography>
        </Breadcrumbs>
      </Paper>

      {/* Header */}
      <Fade in={true}>
        <Paper elevation={6} sx={{ 
          mb: 4, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none'
          }
        }}>
          <Box sx={{ 
            p: { xs: 3, md: 4 }, 
            position: 'relative',
            zIndex: 1 
          }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.3)'
                  }}>
                    {getTaskTypeIcon(task.taskType)}
                  </Avatar>
                  <Box>
                    <Typography variant="h2" fontWeight={900} sx={{ 
                      color: 'white',
                      fontSize: { xs: '2rem', md: '2.8rem' },
                      mb: 0.5,
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)'
                    }}>
                      {task.taskNumber || `Task #${task._id?.slice(-6)}`}
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <CalendarToday sx={{ fontSize: 20 }} />
                      Created on {new Date(task.createdAt).toLocaleDateString()}
                      {task.createdBy && ` by ${task.createdBy.first_name} ${task.createdBy.last_name}`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      onClick={loadTaskData}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        '&:hover': { 
                          backgroundColor: 'rgba(255,255,255,0.3)',
                          transform: 'rotate(180deg)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    sx={{ 
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': { 
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/hospital/housekeeping-and-linen-management/housekeeping/task-edit/${task._id}`)}
                    sx={{ 
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': { 
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Edit Task
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={getStatusIcon(task.status)}
                    onClick={() => {
                      setSelectedStatus(task.status);
                      setStatusDialogOpen(true);
                    }}
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      '&:hover': { 
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Update Status
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Fade>

      {/* Overdue Alert */}
      {isOverdue() && (
        <Zoom in={true}>
          <Alert 
            severity="error" 
            icon={<Warning sx={{ fontSize: 30 }} />}
            sx={{ 
              mb: 4, 
              borderRadius: 3,
              fontSize: '1.1rem',
              boxShadow: '0 4px 20px rgba(244, 67, 54, 0.2)',
              '& .MuiAlert-message': { fontSize: '1.1rem' }
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
              ⚠️ This task is overdue!
            </Typography>
            <Typography>
              Due date was <strong>{new Date(task.dueAt).toLocaleDateString()}</strong> at{' '}
              <strong>{new Date(task.dueAt).toLocaleTimeString()}</strong>
            </Typography>
          </Alert>
        </Zoom>
      )}

      <Grid container spacing={3}>
        {/* Main Content - Left Side */}
        <Grid item xs={12} md={8}>
          {/* Task Overview Card */}
          <Slide direction="right" in={true}>
            <Card sx={{ 
              mb: 3, 
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <Info color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h4" fontWeight={700} sx={{ 
                    fontSize: '1.6rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Task Overview
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Task Type
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        {getTaskTypeIcon(task.taskType)}
                        <Typography variant="body1" fontWeight={600}>
                          {task.taskType ? task.taskType.replace('_', ' ') : 'Not specified'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Priority
                      </Typography>
                      <Chip
                        label={task.priority}
                        color={getPriorityColor(task.priority)}
                        size="medium"
                        icon={<PriorityHigh />}
                        sx={{ 
                          textTransform: 'capitalize',
                          fontWeight: 600,
                          px: 1
                        }}
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Status
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Chip
                          label={task.status ? task.status.replace('_', ' ') : 'pending'}
                          color={getStatusColor(task.status)}
                          size="medium"
                          icon={getStatusIcon(task.status)}
                          sx={{ 
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            px: 1
                          }}
                        />
                        {isOverdue() && (
                          <Chip
                            label="Overdue"
                            color="error"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Due Date
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {task.dueAt ? new Date(task.dueAt).toLocaleDateString() : 'Not set'}
                      </Typography>
                      {task.dueAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <AccessTime fontSize="small" />
                          {new Date(task.dueAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Slide>

          {/* Room Information Card */}
          <Slide direction="right" in={true} style={{ transitionDelay: '100ms' }}>
            <Card sx={{ 
              mb: 3, 
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <LocationOn color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h4" fontWeight={700} sx={{ 
                    fontSize: '1.6rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Room Information
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  {[
                    { label: 'Room Number', value: task.room?.room_no || 'Not specified', icon: <Room /> },
                    { label: 'Location', value: task.room?.location || 'Not specified', icon: <LocationOn /> },
                    { label: 'Ward', value: task.ward?.name || task.room?.ward?.name || 'Not specified', icon: <LocalHospital /> },
                    { label: 'Floor', value: task.floor?.name || task.room?.floor?.name || 'Not specified', icon: <Work /> },
                    ...(task.room?.beds ? [{
                      label: 'Bed Status', 
                      value: `${task.room.occupied_beds || 0} / ${task.room.bed_count || 0} occupied`,
                      icon: <Bed />
                    }] : [])
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper elevation={0} sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.1)'
                        }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {React.cloneElement(item.icon, { color: 'primary', sx: { fontSize: 20 } })}
                          <Typography variant="body2" color="text.secondary">
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={600}>
                          {item.value}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Slide>

          {/* Checklist Information */}
          {task.checklist && (
            <Slide direction="right" in={true} style={{ transitionDelay: '200ms' }}>
              <Card sx={{ 
                mb: 3, 
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: 'primary.light'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Assignment color="primary" sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ 
                          fontSize: '1.6rem',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5
                        }}>
                          Checklist: {task.checklist.title}
                        </Typography>
                        {task.checklist.description && (
                          <Typography variant="body2" color="text.secondary">
                            {task.checklist.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton 
                      onClick={() => toggleSection('checklist')}
                      sx={{ 
                        backgroundColor: 'primary.light',
                        '&:hover': { backgroundColor: 'primary.main', color: 'white' }
                      }}
                    >
                      {expandedSection.checklist ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedSection.checklist}>
                    {task.checklist.items && task.checklist.items.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ 
                          mb: 2, 
                          color: 'primary.main',
                          fontWeight: 600
                        }}>
                          📋 Checklist Items ({task.checklist.items.length})
                        </Typography>
                        <Grid container spacing={2}>
                          {task.checklist.items.map((item, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Paper elevation={1} sx={{ 
                                p: 2, 
                                borderRadius: 2,
                                borderLeft: '4px solid',
                                borderColor: item.required ? 'error.main' : 'success.main',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateX(4px)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }
                              }}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                  {item.label}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                  <Chip 
                                    label={`Type: ${item.type}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                  />
                                  <Chip 
                                    label={`Required: ${item.required ? "Yes" : "No"}`}
                                    size="small"
                                    variant="outlined"
                                    color={item.required ? "error" : "success"}
                                  />
                                </Box>

                                {item.type === "select" && item.options?.length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Options:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {item.options.map((option, optIndex) => (
                                        <Chip 
                                          key={optIndex}
                                          label={option}
                                          size="small"
                                          variant="outlined"
                                          color="default"
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            </Slide>
          )}

          {/* Photos Section */}
          <Slide direction="right" in={true} style={{ transitionDelay: '300ms' }}>
            <Card sx={{ 
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <PhotoLibrary color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h4" fontWeight={700} sx={{ 
                    fontSize: '1.6rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Photos & Documentation
                  </Typography>
                </Box>

                {/* Photo Upload */}
                <Paper elevation={0} sx={{ 
                  mb: 4, 
                  p: 3, 
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: 'primary.light'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    mb: 2 
                  }}>
                    <CloudUpload color="primary" />
                    Upload New Photos
                  </Typography>
                  
                  <input
                    type="file"
                    multiple
                    onChange={handleFilesChange}
                    accept="image/*"
                    id="photo-upload"
                    style={{ display: 'none' }}
                  />
                  
                  <label htmlFor="photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCamera />}
                      sx={{ 
                        mb: 2,
                        width: '100%',
                        py: 2,
                        borderStyle: 'dashed',
                        '&:hover': {
                          borderStyle: 'solid',
                          backgroundColor: 'primary.light'
                        }
                      }}
                    >
                      {files.length > 0 ? `${files.length} file(s) selected` : 'Click to select photos'}
                    </Button>
                  </label>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <PhotoCamera />}
                      onClick={() => handleFileUpload(true)}
                      disabled={files.length === 0 || uploading}
                      sx={{ 
                        flex: 1,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload Before Photos'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <PhotoCamera />}
                      onClick={() => handleFileUpload(false)}
                      disabled={files.length === 0 || uploading}
                      sx={{ 
                        flex: 1,
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(245, 87, 108, 0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload After Photos'}
                    </Button>
                  </Box>
                </Paper>

                <Divider sx={{ my: 4, borderWidth: 2, borderColor: 'divider' }} />

                {/* Photos Display */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: 'rgba(0,0,0,0.02)' }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)} 
                    sx={{ 
                      mb: 3,
                      '& .MuiTab-root': {
                        fontSize: '1rem',
                        fontWeight: 600
                      }
                    }}
                    variant="fullWidth"
                  >
                    <Tab 
                      label={
                        <Badge 
                          badgeContent={task.before_photos?.length || 0} 
                          color="primary"
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhotoCamera />
                            Before Photos
                          </Box>
                        </Badge>
                      } 
                    />
                    <Tab 
                      label={
                        <Badge 
                          badgeContent={task.after_photos?.length || 0} 
                          color="secondary"
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhotoCamera />
                            After Photos
                          </Box>
                        </Badge>
                      } 
                    />
                  </Tabs>

                  {activeTab === 0 ? (
                    <PhotoSection
                      title="Before Photos"
                      photos={task.before_photos || []}
                      emptyMessage="No before photos uploaded yet"
                    />
                  ) : (
                    <PhotoSection
                      title="After Photos"
                      photos={task.after_photos || []}
                      emptyMessage="No after photos uploaded yet"
                    />
                  )}
                </Paper>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Sidebar - Right Side */}
        <Grid item xs={12} md={4}>
          {/* Assigned Staff Card */}
          <Slide direction="left" in={true}>
            <Card sx={{ 
              mb: 3, 
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <Person color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h4" fontWeight={700} sx={{ 
                    fontSize: '1.6rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Assigned Staff
                  </Typography>
                </Box>

                {task.assignedTo ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        margin: '0 auto 20px',
                        border: '4px solid',
                        borderColor: 'primary.main',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        fontSize: '2.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {task.assignedTo.first_name?.[0]}{task.assignedTo.last_name?.[0]}
                    </Avatar>
                    
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                      {task.assignedTo.first_name} {task.assignedTo.last_name}
                    </Typography>
                    
                    <Chip
                      label={task.assignedTo.role || 'Housekeeping Staff'}
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    
                    <Stack spacing={2} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {task.assignedTo.email}
                        </Typography>
                      </Box>
                      
                      {task.assignedTo.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {task.assignedTo.phone}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                    
                    {/* <Button
                      variant="contained"
                      startIcon={<Chat />}
                      fullWidth
                      sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Contact Staff
                    </Button> */}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Person sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Not assigned to any staff member
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Person />}
                      onClick={() => navigate(`/hospital/housekeeping-and-linen-management/housekeeping/task-edit/${task._id}`)}
                    >
                      Assign Staff
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Slide>

          {/* Task Timeline Card */}
          <Slide direction="left" in={true} style={{ transitionDelay: '100ms' }}>
            <Card sx={{ 
              mb: 3, 
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Timeline color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h4" fontWeight={700} sx={{ 
                      fontSize: '1.6rem',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      Task Timeline
                    </Typography>
                  </Box>
                  <IconButton 
                    onClick={() => toggleSection('timeline')}
                    sx={{ 
                      backgroundColor: 'primary.light',
                      '&:hover': { backgroundColor: 'primary.main', color: 'white' }
                    }}
                  >
                    {expandedSection.timeline ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSection.timeline}>
                  <Stack spacing={2}>
                    {[
                      { label: 'Created', time: task.createdAt, icon: <CalendarToday color="primary" /> },
                      ...(task.startedAt ? [{ label: 'Started', time: task.startedAt, icon: <PlayArrow color="warning" /> }] : []),
                      ...(task.completedAt ? [{ label: 'Completed', time: task.completedAt, icon: <CheckCircle color="success" /> }] : []),
                      { label: 'Last Updated', time: task.updatedAt, icon: <Refresh color="info" /> }
                    ].map((item, index) => (
                      <Paper 
                        key={index} 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateX(4px)',
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {item.icon}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {item.label}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {new Date(item.time).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(item.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                    
                    {task.duration && (
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Speed color="primary" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Duration
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {task.duration} minutes
                            </Typography>
                          </Box>
                          <DoneAll color="success" />
                        </Box>
                      </Paper>
                    )}
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          </Slide>

          {/* Quality Audits Card */}
          <Slide direction="left" in={true} style={{ transitionDelay: '200ms' }}>
            <Card sx={{ 
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Grade color="primary" sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="h4" fontWeight={700} sx={{ 
                        fontSize: '1.6rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5
                      }}>
                        Quality Audits
                      </Typography>
                      <Chip
                        label={`${audits.length} audit(s)`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <IconButton 
                    onClick={() => toggleSection('audits')}
                    sx={{ 
                      backgroundColor: 'primary.light',
                      '&:hover': { backgroundColor: 'primary.main', color: 'white' }
                    }}
                  >
                    {expandedSection.audits ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSection.audits}>
                  {audits.length > 0 ? (
                    <Table size="small" sx={{ 
                      '& .MuiTableCell-root': { 
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }
                    }}>
                      <TableHead>
                        <TableRow sx={{ 
                          backgroundColor: 'primary.light',
                          '& .MuiTableCell-head': { 
                            color: 'primary.main',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }
                        }}>
                          <TableCell>Date</TableCell>
                          <TableCell>Inspector</TableCell>
                          <TableCell align="center">Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {audits.slice(0, 5).map((audit) => (
                          <TableRow 
                            key={audit._id} 
                            hover
                            sx={{ 
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'primary.light'
                              }
                            }}
                          >
                            <TableCell>
                              {new Date(audit.inspectedAt || audit.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {audit.inspectedBy
                                ? `${audit.inspectedBy.first_name} ${audit.inspectedBy.last_name}`
                                : audit.inspector || 'Unknown'
                              }
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={audit.score ? `${audit.score}%` : 'N/A'}
                                size="small"
                                color={audit.score >= 90 ? 'success' : audit.score >= 70 ? 'warning' : 'error'}
                                icon={audit.score >= 90 ? <ThumbUp /> : audit.score >= 70 ? <ThumbUp /> : <ThumbDown />}
                                sx={{ 
                                  fontWeight: 600,
                                  minWidth: 70
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Grade sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        No quality audits conducted for this task
                      </Typography>
                    </Box>
                  )}

                  {audits.length > 5 && (
                    <Button
                      fullWidth
                      variant="text"
                      size="small"
                      endIcon={<Visibility />}
                      sx={{ 
                        mt: 2,
                        color: 'primary.main',
                        fontWeight: 600
                      }}
                    >
                      View All {audits.length} Audits
                    </Button>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Status Update Dialog */}
      <Dialog 
        open={statusDialogOpen} 
        onClose={() => setStatusDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center'
        }}>
          <DialogTitle sx={{ 
            color: 'white', 
            p: 0,
            fontSize: '1.5rem',
            fontWeight: 700
          }}>
            Update Task Status
          </DialogTitle>
        </Box>
        
        <DialogContent sx={{ p: 4 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ 
                '& .MuiSelect-select': { 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              }}
            >
              {[
                { value: 'pending', label: '⏳ Pending', color: 'default' },
                { value: 'in_progress', label: '🔄 In Progress', color: 'warning' },
                { value: 'completed', label: '✅ Completed', color: 'success' },
                { value: 'cancelled', label: '❌ Cancelled', color: 'error' }
              ].map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={option.label} 
                      size="small" 
                      color={option.color}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedStatus === 'completed' && (
            <Alert 
              severity="info" 
              icon={<Info />}
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                fontSize: '0.9rem'
              }}
            >
              Marking as completed will set the completion timestamp and calculate task duration.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setStatusDialogOpen(false)} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStatusUpdate} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper Components
const PhotoSection = ({ title, photos, emptyMessage }) => (
  <Box>
    <Typography variant="subtitle1" gutterBottom sx={{ 
      mb: 2,
      color: 'primary.main',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }}>
      <PhotoLibrary />
      {title}
    </Typography>
    {photos.length > 0 ? (
      <Grid container spacing={2}>
        {photos.map((photo, index) => (
          <Grid item xs={6} sm={4} md={3} key={index}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
                  '& .overlay': {
                    opacity: 1
                  }
                }
              }}
              onClick={() => window.open(`${BASE_URL_FOR_FILE}${photo}`, '_blank')}
            >
              <img
                src={`${BASE_URL_FOR_FILE}${photo}`}
                alt={`${title.toLowerCase()} ${index + 1}`}
                style={{
                  width: '100%',
                  height: 120,
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              <Box
                className="overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Tooltip title="View Full Size">
                  <IconButton
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'primary.main',
                      '&:hover': { 
                        backgroundColor: 'white',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`${BASE_URL_FOR_FILE}${photo}`, '_blank');
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  color: 'white',
                  p: 0.5,
                  fontSize: '0.75rem',
                  textAlign: 'center'
                }}
              >
                Photo {index + 1}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    ) : (
      <Paper elevation={0} sx={{ 
        p: 6, 
        textAlign: 'center',
        borderRadius: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <PhotoLibrary sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {emptyMessage}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upload photos using the upload section above
        </Typography>
      </Paper>
    )}
  </Box>
);

const AuditsCard = ({ audits }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Quality Audits
        </Typography>
        <Chip
          label={`${audits.length} audit(s)`}
          size="small"
          variant="outlined"
        />
      </Box>

      {audits.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Inspector</TableCell>
              <TableCell align="center">Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audits.slice(0, 5).map((audit) => (
              <TableRow key={audit._id} hover>
                <TableCell>
                  {new Date(audit.inspectedAt || audit.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {audit.inspectedBy
                    ? `${audit.inspectedBy.first_name} ${audit.inspectedBy.last_name}`
                    : audit.inspector || 'Unknown'
                  }
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={audit.score ? `${audit.score}%` : 'N/A'}
                    size="small"
                    color={audit.score >= 90 ? 'success' : audit.score >= 70 ? 'warning' : 'error'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
          No quality audits conducted for this task
        </Typography>
      )}

      {audits.length > 5 && (
        <Button
          fullWidth
          variant="text"
          size="small"
          sx={{ mt: 1 }}
          onClick={() => {/* Navigate to full audits page */ }}
        >
          View All {audits.length} Audits
        </Button>
      )}
    </CardContent>
  </Card>
);

export default TaskDetail;